// src/pages/CookiesPage.jsx
import React from "react";

export default function CookiesPage() {
  return (
    <div className="bg-white text-black min-h-screen">
      <div className="max-w-3xl mx-auto px-4 py-12 md:py-16 text-sm leading-relaxed md:text-base md:leading-relaxed">
        <header className="mb-10">
          <h1 className="text-2xl md:text-3xl font-serif mb-2">
            Politique relative aux cookies
          </h1>
          <div className="text-gray-500 text-xs md:text-sm">
            Dernière mise à jour : août 2026
          </div>
        </header>

        <section className="space-y-6 text-gray-800">
          <p>
            La présente politique explique comment <strong>Artkiv</strong> (QUANG
            &amp; CO, 231 rue Saint-Honoré, 75001 Paris) utilise des cookies et
            autres traceurs lors de votre navigation sur artkiv.com et ses
            sous-domaines, notamment vision.artkiv.com.
          </p>

          <p>
            Un cookie est un petit fichier texte déposé sur votre appareil
            (ordinateur, tablette, téléphone) lorsque vous visitez un site.
            Artkiv est un outil de gestion patrimoniale : nous n’utilisons que
            les traceurs strictement nécessaires au fonctionnement du service,
            à l’exclusion de tout cookie publicitaire ou de mesure d’audience.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            1. Cookies et traceurs utilisés
          </h2>

          <div className="space-y-3">
            <h3 className="font-medium">
              a. Cookies et stockage strictement nécessaires
            </h3>
            <p className="text-gray-700">
              Ces traceurs sont indispensables au fonctionnement du site et à la
              fourniture du service :
            </p>
            <ul className="list-disc ml-5 text-gray-700">
              <li>
                Connexion et maintien de session sécurisée (y compris le partage
                de session entre artkiv.com et vision.artkiv.com)
              </li>
              <li>Protection contre la fraude et les abus</li>
              <li>Fonctionnement du tableau de bord utilisateur</li>
              <li>Gestion des paiements (session Stripe)</li>
            </ul>
            <p className="text-gray-700">
              Ils ne peuvent pas être désactivés car le site ne fonctionnerait
              pas correctement sans eux. Étant strictement nécessaires, ils ne
              requièrent pas votre consentement préalable.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">
              b. Préférences enregistrées sur votre appareil
            </h3>
            <p className="text-gray-700">
              Certains réglages (par exemple votre langue d’affichage ou votre
              accès à la bêta privée) sont conservés localement sur votre
              appareil, via le stockage local du navigateur, afin de ne pas
              avoir à les reconfigurer à chaque visite.
            </p>
            <p className="text-gray-700">
              Ces informations restent sur votre appareil et ne servent ni à
              vous suivre entre les sites, ni à vous profiler.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">
              c. Absence de cookies analytiques
            </h3>
            <p className="text-gray-700">
              Artkiv ne dépose actuellement aucun cookie de mesure d’audience ni
              d’outil d’analyse tiers (par exemple Google Analytics, Matomo,
              Plausible). Si nous venions à en introduire, cette politique serait
              mise à jour et une bannière de consentement serait affichée au
              préalable pour recueillir votre accord.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">
              d. Absence de cookies publicitaires
            </h3>
            <p className="text-gray-700">
              Artkiv n’utilise pas de cookies publicitaires ni de traceurs
              marketing pour du ciblage commercial.
            </p>
          </div>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            2. Consentement
          </h2>
          <p className="text-gray-700">
            Dans la mesure où Artkiv n’utilise que des cookies strictement
            nécessaires et des préférences stockées localement sur votre
            appareil, aucune bannière de consentement n’est requise et aucune
            n’est affichée.
          </p>
          <p className="text-gray-700">
            Si des cookies optionnels (par exemple de mesure d’audience) étaient
            introduits à l’avenir, une bannière vous permettrait de les accepter,
            de les refuser ou de choisir vos préférences en détail, et votre
            choix serait conservé pour une durée maximale de six (6) mois.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            3. Cookies déposés par des tiers
          </h2>
          <p className="text-gray-700">
            Les services techniques strictement nécessaires utilisés par Artkiv
            peuvent déposer leurs propres cookies, à savoir :
          </p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>Paiement sécurisé et lutte contre la fraude (Stripe)</li>
            <li>Authentification et gestion de session (Supabase)</li>
          </ul>
          <p className="text-gray-700">
            Ces tiers n’ont pas l’autorisation d’utiliser ces informations pour
            du ciblage publicitaire au nom d’Artkiv.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            4. Paramétrer ou désactiver les cookies via votre navigateur
          </h2>
          <p className="text-gray-700">
            Vous pouvez bloquer, limiter ou supprimer les cookies via les
            paramètres de votre navigateur. Le blocage des cookies essentiels
            peut empêcher certaines fonctionnalités (connexion, tableau de
            bord, paiement).
          </p>
          <p className="text-gray-700">
            Pour plus d’informations, consultez la documentation de votre
            navigateur (Chrome, Safari, Firefox, Edge, etc.).
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            5. Durée de conservation
          </h2>
          <p className="text-gray-700">
            Les cookies de session et d’authentification sont conservés pour la
            durée nécessaire au maintien de votre connexion (jusqu’à douze mois),
            puis renouvelés ou supprimés. Les préférences enregistrées localement
            (langue, réglages) restent sur votre appareil jusqu’à ce que vous les
            effaciez via votre navigateur.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">6. Contact</h2>
          <p className="text-gray-700">
            Pour toute question relative à l’utilisation des cookies, ou pour
            exercer vos droits d’accès / opposition / effacement :
          </p>
          <p className="text-gray-700">
            E-mail : <strong>privacy@artkiv.com</strong>
            <br />
            Adresse : QUANG &amp; CO — 231 rue Saint-Honoré, 75001 Paris
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-serif">7. Langue faisant foi</h2>
          <p className="text-gray-700">
            La version française des accords juridiques et des politiques est
            considérée comme la seule version actuelle et valide de ce document.
            Toute version traduite n’est fournie que pour votre commodité, afin de
            faciliter la lecture et la compréhension de la version française. Les
            versions traduites ne sont pas juridiquement contraignantes et ne
            peuvent remplacer les versions françaises. En cas de désaccord ou de
            conflit, les accords et politiques juridiques en langue française
            prévaudront.
          </p>
        </section>

        <footer className="mt-12 text-[11px] text-gray-400 leading-relaxed">
          Artkiv — Outil de gestion patrimoniale et de traçabilité des œuvres
          d’art. Aucun cookie publicitaire ni de mesure d’audience.
        </footer>
      </div>
    </div>
  );
}
